<template>
  <div class="grid-container container is-widescreen">
    <div class="tw-bg-white tw-shadow-sm tw-rounded-lg tw-max-w-2xl tw-mx-auto tw-mb-8 tw-p-8 tw-text-center">
      <o-button
        v-if="isCreditBasedFirm && canPurchaseCredits"
        variant="primary"
        size="large"
        class="tw-px-12 tw-mb-4"
        @click="isPaymentModalVisible = true"
      >
        <span>Buy More Credits</span>
      </o-button>

      <div v-if="isCreditBasedFirm" class="tw-text-lg">
        <span class="tw-text-gray-600">Credits Remaining:</span>
        <span class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-ml-2">{{ creditsRemaining }}</span>
      </div>
    </div>

    <!-- Payment Modal -->
    <div
      v-if="isPaymentModalVisible"
      class="modal"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Purchase Confirmation
          </p>
          <button
            type="button"
            class="delete"
            @click="isPaymentModalVisible = false"
          />
        </header>
        <div class="modal-card-body tw-text-black">
          <p>Please confirm, this will charge your card on file $1,000.</p>
        </div>
        <footer class="modal-card-foot">
          <button
            class="button is-success"
            @click="purchaseCredits"
          >
            Confirm
          </button>
          <button
            class="button"
            @click="isPaymentModalVisible = false"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>

    <!-- Statement Table Section -->
    <div id="header" class="has-background-white-ter p-6">
      <div class="mb-6">
        <h4 class="title is-6">Brokerage Statement Uploads</h4>
        <h5
          v-if="!isFlatPricing"
          class="subtitle is-6"
        >
          *NOTE: Current month's classifications are subject to change based on monthly review
        </h5>

          <!-- Brokerage Statement Uploads -->
          <o-table
            :key="isFlatPricing ? 'flat' : 'split'"
            :data="tableDataWithTotals"
            sortable
            hoverable
          >
            <!-- Month -->
            <o-table-column field="month" label="Month">
              <template v-slot="props">
                <template v-if="!props.row.isTotal">
                  {{ props.row.month }}/{{ props.row.year }}
                </template>
                <template v-else>
                  <strong>{{ props.row.month }}</strong>
                </template>
              </template>
            </o-table-column>

            <!-- Flat pricing: single count -->
            <o-table-column
              v-if="isFlatPricing"
              field="numDocuments"
              label="Total Number of Statements"
            >
              <template v-slot="props">
                <strong v-if="props.row.isTotal">{{ props.row.numDocuments }}</strong>
                <template v-else>{{ props.row.numDocuments }}</template>
              </template>
            </o-table-column>

            <!-- Non-flat pricing: split counts -->
            <o-table-column
              v-if="!isFlatPricing"
              field="numStandard"
              label="Number of Standard Statements"
            >
              <template v-slot="props">
                <strong v-if="props.row.isTotal">{{ props.row.numStandard }}</strong>
                <template v-else>{{ props.row.numStandard }}</template>
              </template>
            </o-table-column>

            <o-table-column
              v-if="!isFlatPricing"
              field="numAdvanced"
              label="Number of Advanced Statements"
            >
              <template v-slot="props">
                <strong v-if="props.row.isTotal">{{ props.row.numAdvanced }}</strong>
                <template v-else>{{ props.row.numAdvanced }}</template>
              </template>
            </o-table-column>

            <!-- Total Cost (always shown) -->
            <o-table-column field="totalCost" label="Total Cost">
              <template v-slot="props">
                <strong v-if="props.row.isTotal">
                  ${{ Number(props.row.totalCost || 0).toFixed(2) }}
                </strong>
                <template v-else>
                  ${{ Number(props.row.totalCost || 0).toFixed(2) }}
                </template>
              </template>
            </o-table-column>

            <!-- Download Details -->
            <o-table-column label="Download Details">
              <template v-slot="props">
                <button
                  v-if="!props.row.isTotal"
                  class="button is-small is-link"
                  @click="downloadCSV(props.row.month, props.row.year)"
                >
                  Download CSV
                </button>
              </template>
            </o-table-column>
          </o-table>

      </div>
    </div>
    
    <!-- Document Upload Table Section -->
    <div id="docupload-header" class="has-background-white-ter p-6">
      <div class="mb-6">
        <h4 class="title is-6">General Document Uploads</h4>
        <o-table :data="docuploadTableWithTotals" sortable hoverable>
          <o-table-column field="month" label="Month">
            <template v-slot="props">
              <template v-if="!props.row.isTotal">
                {{ props.row.month }}/{{ props.row.year }}
              </template>
              <template v-else>
                <strong>{{ props.row.month }}</strong>
              </template>
            </template>
          </o-table-column>
          
          <o-table-column field="numDocuments" label="Number of Documents">
            <template v-slot="props">
              <strong v-if="props.row.isTotal">{{ props.row.numDocuments }}</strong>
              <template v-else>{{ props.row.numDocuments }}</template>
            </template>
          </o-table-column>
          
          <o-table-column field="totalCost" label="Total Cost">
            <template v-slot="props">
              <strong v-if="props.row.isTotal">${{ props.row.totalCost.toFixed(2) }}</strong>
              <template v-else>${{ props.row.totalCost.toFixed(2) }}</template>
            </template>
          </o-table-column>
          
          <o-table-column label="Download Details">
            <template v-slot="props">
              <button
                v-if="!props.row.isTotal"
                class="button is-small is-link"
                @click="downloadDocuploadCSV(props.row.month, props.row.year)"
              >
                Download CSV
              </button>
            </template>
          </o-table-column>
        </o-table>
      </div>
    </div>

    <!-- Special Credits Table Section -->
    <div id="special-credits-header" class="has-background-white-ter p-6">
      <div class="mb-6">
        <h4 class="title is-6">Special Credits Usage</h4>
        <o-table :data="specialCreditsWithTotals" sortable hoverable>
          <!-- Name -->
          <o-table-column field="name" label="Name">
            <template v-slot="props">
              <template v-if="!props.row.isTotal">
                {{ props.row.name }}
              </template>
              <strong v-else>{{ props.row.name }}</strong>
            </template>
              </o-table-column>

              <!-- Description -->
              <o-table-column field="description" label="Description">
            <template v-slot="props">
              {{ props.row.description }}
            </template>
              </o-table-column>

              <!-- Credits Used -->
              <o-table-column field="credits_used" label="Credits Used">
            <template v-slot="props">
              <template v-if="!props.row.isTotal">
                {{ props.row.credits_used }}
              </template>
              <strong v-else>{{ props.row.credits_used }}</strong>
            </template>
              </o-table-column>

              <!-- Download CSV -->
              <o-table-column label="Download CSV">
            <template v-slot="props">
              <template v-if="props.row.isTotal">
                <button
              class="button is-small is-link"
              @click="downloadSpecialCreditsCSV"
                >
              Download CSV
                </button>
              </template>
            </template>
          </o-table-column>
        </o-table>
      </div>
      </div>

    <!-- Meetings Table Section -->
    <div id="meeting-header" class="has-background-white-ter p-6">
      <div class="mb-6">
        <h4 class="title is-6">Meetings</h4>
        <o-table :data="meetingTableWithTotals" sortable hoverable>
          <o-table-column field="month" label="Month">
            <template v-slot="props">
              <template v-if="!props.row.isTotal">
                {{ props.row.month }}/{{ props.row.year }}
              </template>
              <template v-else>
                <strong>{{ props.row.month }}</strong>
              </template>
            </template>
          </o-table-column>
          <o-table-column field="numMeetings" label="Number of Meetings">
            <template v-slot="props">
              <strong v-if="props.row.isTotal">{{ props.row.numMeetings }}</strong>
              <template v-else>{{ props.row.numMeetings }}</template>
            </template>
          </o-table-column>
          <o-table-column field="totalCost" label="Total Cost">
            <template v-slot="props">
              <strong v-if="props.row.isTotal">${{ Number(props.row.totalCost || 0).toFixed(2) }}</strong>
              <template v-else>${{ Number(props.row.totalCost || 0).toFixed(2) }}</template>
            </template>
          </o-table-column>
          <o-table-column label="Download Details">
            <template v-slot="props">
              <button
                v-if="!props.row.isTotal"
                class="button is-small is-link"
                @click="downloadMeetingsCSV(props.row.month, props.row.year)"
              >
                Download CSV
              </button>
            </template>
          </o-table-column>
        </o-table>
      </div>
    </div>

  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';

export default {
  name: "MonthlyStatementTables",
  data() {
    return {
      statementCredits: [],
      docuploadCredits: [],
      meetingCredits: [],
      specialCredits: [],
      isPaymentModalVisible: false,
      creditsRemaining: 0,
    };
  },
  watch: {
    'advisorStore.advisor.firm.extraction_credits'(newVal) {
      this.creditsRemaining = newVal;
    },
  },
  computed: {
    ...mapStores(useAdvisorStore),
    isCreditBasedFirm() {
      return this.advisorStore.advisor?.firm?.credit_based_firm || false;
    },
    canPurchaseCredits() {
      return this.advisorStore.advisor?.can_purchase_credits || false;
    },
    isFlatPricing() {
      const test = this.advisorStore.advisor?.firm?.flat_statement_pricing 
      console.log('IS FLAT PRICING?', test)
      return this.advisorStore.advisor?.firm?.flat_statement_pricing === true;
    },
    
    specialCreditsWithTotals() {
      // mark each row
      const regular = this.specialCredits.map(item => ({ ...item, isTotal: false }));
      // sum up credits_used
      const total = this.specialCredits
        .reduce((sum, item) => sum + (Number(item.credits_used) || 0), 0);
      const totalsRow = {
        name: 'TOTALS',
        description: '',
        credits_used: total,
        isTotal: true
      };
      return [...regular, totalsRow];
    },
    aggregatedData() {
      const result = {};

      this.statementCredits.forEach((item) => {
        const date = new Date(item.upload_date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const key = `${month}-${year}`;

        if (!result[key]) {
          result[key] = {
            month,
            year,
            totalCost: 0,
            numStandard: 0,
            numAdvanced: 0,
            numZeroDollar: 0,
            numDocuments: 0, 
            rows: [],
          };
        }

        const originalCost = item.cost; // Store the original cost
        const parsedCost = parseFloat(typeof item.cost === "string" ? item.cost.replace("$", "") : item.cost) ?? 3; // if can't parse cost, we assume standard
        result[key].totalCost += parsedCost;
        // account for each clasification, listed here: CLASSIFICATION_MAP = dict(FutureModelPortfolio.CLASSIFICATION_CHOICES)
        if (parsedCost === 0){ // sample statements can be classified as standard but we charge them as $0
          result[key].numZeroDollar += 1;
        } else if (item.document_classification === "Standard") {
          result[key].numStandard += 1;
        } else if (item.document_classification === "Missing Tickers") {
            result[key].numStandard += 1;
        } else if (item.document_classification === "Advanced") {
          result[key].numAdvanced += 1;
        } else if (item.document_classification === "Bad Scan/Image"){
          result[key].numAdvanced += 1;
        } else if (item.document_classification === "Incomplete"){
            result[key].numAdvanced += 1;
        } else if (item.document_classification === "Non-brokerage statement"){
          result[key].numAdvanced += 1;
        } else if (item.document_classification === "No Holdings"){
          result[key].numStandard += 1; // adding to standard bc these are also charged as $3
        } else if (item.document_classification === "na"){
          result[key].numZeroDollar += 1;
        } else {
          result[key].numStandard += 1; // shouldn't get here, everything else classified as standard
        }
        result[key].numDocuments += 1; // track total number of uploads
        result[key].rows.push(item);
      });

      return Object.values(result).sort(
        (a, b) => b.year - a.year || b.month - a.month
      );
    },
    aggregatedDocuploadData() {
      const result = {};

      this.docuploadCredits.forEach((item) => {
        const date = new Date(item.upload_date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const key = `${month}-${year}`;

        if (!result[key]) {
          result[key] = {
            month,
            year,
            totalCost: 0,
            numDocuments: 0,
            numZeroDollar: 0,
            rows: [],
          };
        }

        const parsedCost = parseFloat(typeof item.cost === "string" ? item.cost.replace("$", "") : item.cost) ?? 0;
        result[key].totalCost += parsedCost;
        
        if (parsedCost === 0) {
          result[key].numZeroDollar += 1;
        }
        
        result[key].numDocuments += 1;
        result[key].rows.push(item);
      });

      return Object.values(result).sort(
        (a, b) => b.year - a.year || b.month - a.month
      );
    },
    tableData() {
      return this.aggregatedData;
    },
    docuploadTableData() {
      return this.aggregatedDocuploadData;
    },
    tableDataWithTotals() {
    const regularData = this.tableData;

    const totalsBase = {
      month: 'TOTALS',
      year: '',
      totalCost: this.totalCost,
      isTotal: true,
    };

    const totalsRow = this.isFlatPricing
      ? {
          ...totalsBase,
          // flat pricing: single count of statements
          numDocuments: regularData.reduce((sum, row) => sum + (row.numDocuments || 0), 0),
        }
      : {
          ...totalsBase,
          // for firms that charge for standard vs advanced
          numStandard: this.totalStandard,
          numAdvanced: this.totalAdvanced,
          numZeroDollar: this.totalZeroDollar,
        };

    return [...regularData, totalsRow];
  },

    docuploadTableWithTotals() {
      const regularData = this.docuploadTableData;
      const totalsRow = {
        month: 'TOTALS',
        year: '',
        numDocuments: this.totalDocuments,
        numZeroDollar: this.totalDocuploadZeroDollar,
        totalCost: this.totalDocuploadCost,
        isTotal: true
      };
      
      return [...regularData, totalsRow];
    },
    totalCost() {
      return this.tableData.reduce((sum, row) => sum + row.totalCost, 0);
    },
    totalDocuploadCost() {
      return this.docuploadTableData.reduce((sum, row) => sum + row.totalCost, 0);
    },
    totalStandard() {
      return this.tableData.reduce((sum, row) => sum + row.numStandard, 0);
    },
    totalAdvanced() {
      return this.tableData.reduce((sum, row) => sum + row.numAdvanced, 0);
    },
    totalDocuments() {
      return this.docuploadTableData.reduce((sum, row) => sum + row.numDocuments, 0);
    },
    totalZeroDollar() {
      return this.tableData.reduce((sum, row) => sum + row.numZeroDollar, 0);
    },
    totalDocuploadZeroDollar() {
      return this.docuploadTableData.reduce((sum, row) => sum + row.numZeroDollar, 0);
    },
    aggregatedMeetingData() {
      const result = {};

      this.meetingCredits.forEach((item) => {
        const date = new Date(item.upload_date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const key = `${month}-${year}`;

        if (!result[key]) {
          result[key] = {
            month,
            year,
            totalCost: 0,
            numMeetings: 0,
            rows: [],
          };
        }

        const parsedCost = parseFloat(typeof item.cost === "string" ? item.cost.replace("$", "") : item.cost) ?? 1;
        result[key].totalCost += parsedCost;
        result[key].numMeetings += 1;
        result[key].rows.push(item);
      });

      return Object.values(result).sort(
        (a, b) => b.year - a.year || b.month - a.month
      );
    },
    meetingTableData() {
      return this.aggregatedMeetingData;
    },
    meetingTableWithTotals() {
      const regularData = this.meetingTableData;
      const totalsRow = {
        month: 'TOTALS',
        year: '',
        numMeetings: this.totalMeetings,
        totalCost: this.totalMeetingCost,
        isTotal: true,
      };
      return [...regularData, totalsRow];
    },
    totalMeetingCost() {
      return this.meetingTableData.reduce((sum, row) => sum + row.totalCost, 0);
    },
    totalMeetings() {
      return this.meetingTableData.reduce((sum, row) => sum + row.numMeetings, 0);
    }
  },
  created() {
    this.fetchStatementDocuploadCredits();
    this.creditsRemaining = this.advisorStore.advisor?.firm?.extraction_credits || 0;
  },
  methods: {
    async fetchStatementDocuploadCredits() {
      try {
        const response = await fetch("/api/v1/get_statement_credits/");
        if (!response.ok) throw new Error("Failed to fetch statement credits");

        const { credits_remaining, data, docupload_data, special_credits_data, meeting_data } = await response.json();
        this.creditsRemaining = credits_remaining;
        this.statementCredits = data;
        this.docuploadCredits = docupload_data;
        this.specialCredits = special_credits_data;
        this.meetingCredits = meeting_data || [];
      } catch (error) {
        console.error("Error fetching statement credits:", error);
      }
    },
    async purchaseCredits() {
      this.isPaymentModalVisible = false;
      try {
        const response = await fetch('/api/v1/checkup/purchase-credits/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
        });
        const result = await response.json();
        if (response.ok && result.is_success) {
          this.creditsRemaining += 1000;
          this.$oruga.notification.open({
            message: 'Credits purchased successfully!',
            variant: 'success',
            position: 'top-right',
            duration: 5000,
          });
        } else {
          this.$oruga.notification.open({
            message: 'Failed to purchase credits',
            variant: 'danger',
            position: 'top-right',
            duration: 5000,
          });
        }
      } catch (error) {
        this.$oruga.notification.open({
          message: 'Failed to purchase credits',
          variant: 'danger',
          position: 'top-right',
          duration: 5000,
        });
      }
    },
    downloadSpecialCreditsCSV() {
      if (!this.specialCredits.length) {
        return alert('No special credits data available.');
      }

      const header = 'Name,Description,Credits Used\n';
      const rows = this.specialCredits.map(item =>
        [item.name, item.description, item.credits_used]
          .map(val => `"${String(val).replace(/"/g, '""')}"`)
          .join(',')
      );
      const csv = header + rows.join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Special_Credits_Usage.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadCSV(month, year) {
      const bucket = this.aggregatedData.find(
        (item) => item.month === month && item.year === year
      );

      if (!bucket || !bucket.rows?.length) {
        alert("No data available for this month.");
        return;
      }

      // Header depends on pricing mode
      const header = this.isFlatPricing
        ? "Upload Date,User Email,Document Name,Cost ($)\n"
        : "Upload Date,User Email,Document Name,Document Classification,Cost ($)\n";

      // Rows: always per-document; omit classification when flat pricing
      const csvRows = bucket.rows.map((item) => {
        const base = [
          item.upload_date,
          item.user_email,
          item.document_name,
        ];

        if (!this.isFlatPricing) {
          base.push(item.document_classification);
        }

        base.push(item.cost);
        return base.join(",");
      });

      const csvContent = header + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = this.isFlatPricing
        ? `Statement_Credits_NoClass_${month}-${year}.csv`
        : `Statement_Credits_${month}-${year}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },


    downloadDocuploadCSV(month, year) {
      const filteredData =
        this.aggregatedDocuploadData.find(
          (item) => item.month === month && item.year === year
        )?.rows || [];

      if (!filteredData.length) {
        alert("No data available for this month.");
        return;
      }

      const csvHeader =
        "Upload Date,Advisor Email,Uploaded By Type,Document Name,Document Classification,Cost ($)\n";
      const csvRows = filteredData.map((item) =>
        [
          item.upload_date,
          item.advisor_email,
          item.uploaded_by_type,
          item.document_name,
          item.document_classification,
          item.cost,
        ].join(",")
      );
      const csvContent = csvHeader + csvRows.join("\n");

      // Create an invisible link and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Docupload_Credits_${month}-${year}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadDocumentCSV(month, year) {
      // This function appears to be unused in your provided code
      // But I'm keeping it in case you need it elsewhere
      console.log(`Downloading document CSV for ${month}/${year}`);
    },
    downloadMeetingsCSV(month, year) {
      const bucket = this.meetingTableData.find(
        (item) => item.month === month && item.year === year
      );

      if (!bucket || !bucket.rows?.length) {
        alert("No data available for this month.");
        return;
      }

      const header = "Upload Date,User Email,Meeting Name,Meeting Classification,Cost ($)\n";
      const csvRows = bucket.rows.map((item) => [
        item.upload_date,
        item.user_email,
        item.meeting_name,
        item.meeting_classification,
        item.cost,
      ].join(","));

      const csvContent = header + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Meeting_Credits_${month}-${year}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
};
</script>

<style scoped>
.grid-container {
  padding: 1rem;
}
button {
  margin-top: 1rem;
}
.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
}
.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 500px;
}
::v-deep .o-table tr:last-child {
  background-color: #f5f5f5;
  border-top: 2px solid #dbdbdb;
}
</style>
